import React from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import { Redirect, useParams } from 'react-router';
import styled from '@emotion/styled';
import {
  BodyTextMedium,
  ComponentTextMedium,
  Grid,
  GridItem,
  HeadingText40,
  HeadingText50,
} from '@modernhealth/vital-components';
import { breakpointValues } from '@modernhealth/vital-foundation';
import { captureException as sentryCaptureException } from '@sentry/react';

import { Head } from 'src/shared/components/Head';
import { BackButton } from 'src/shared/components/BackButton';
import { mediaQueryMinWidth } from 'src/shared/utils';
import useShowSkeletonDelay from 'src/shared/hooks/useShowSkeletonDelay';
import { useTranslation } from 'src/shared/hooks/formatMessage';
import { isPublicTopic } from 'src/shared/utils/isPublicCircle';
import { captureException } from 'src/shared/utils/captureException';

import { useFetchTopicsQuery } from '../documents/topics.generated';
import { CircleTopicFragment } from '../documents/topic.generated';
import { TopicLinkCard } from '../components';
import { SeriesPageSkeleton } from './skeletons/SeriesPageSkeleton';
import { formatSeriesAccessType } from '../utils/formatSeriesAccessType';

const HeroWrapper = styled.div({
  display: 'flex',
  flexDirection: 'column-reverse',
  flexWrap: 'wrap',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  [mediaQueryMinWidth(breakpointValues.md)]: {
    flexDirection: 'row',
  },
});

const Wrapper = styled.div({
  paddingBottom: 100,
});

const HeaderTextWrapper = styled.div({
  width: '100%',
  marginTop: 30,
  [mediaQueryMinWidth(breakpointValues.md)]: {
    width: '50%',
    paddingRight: 50,
    marginTop: 0,
  },
});

const HeaderImageWrapper = styled.div({
  width: '100%',
  borderRadius: 12,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'flex-end',
  [mediaQueryMinWidth(breakpointValues.md)]: {
    width: '50%',
  },
});

const HeaderImage = styled.img({
  width: '100%',
  borderRadius: 12,
});

const messages = {
  header: defineMessages({
    title: { defaultMessage: 'Circles by Modern Health' },
    descriptionFallback: { defaultMessage: 'Explore this series of Circle sessions.' },
  }),
};

interface SeriesProps {
  seriesSlug: string;
}

const SeriesPage: React.FC<SeriesProps> = ({ seriesSlug }) => {
  const { $t } = useTranslation();

  const { data, loading } = useFetchTopicsQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      seriesSlug,
      limit: 100,
    },
  });

  const count = data?.circleSeriesBySlug?.topics?.totalCount;
  if (count && count >= 100) {
    sentryCaptureException(`Circle topics were truncated at 100!`, { extra: { count } });
    captureException(`Circle topics were truncated at 100!`, { extra: { count } });
  }

  const show = useShowSkeletonDelay(!!data);

  if (show || (loading && !data)) {
    return <SeriesPageSkeleton />;
  }

  if (!data?.circleSeriesBySlug) {
    // TODO: Error state

    return <Redirect to="/" />;
  }

  const sessionsCount = data?.circleSeriesBySlug?.topics?.edges
    .map(topic => {
      return topic?.node.sessions?.totalCount || 0;
    })
    .reduce((previousValue, currentValue) => previousValue + currentValue, 0);

  const topics = data.circleSeriesBySlug?.topics?.edges
    .map(edge => edge?.node)
    .filter((node): node is CircleTopicFragment => {
      return node != null && isPublicTopic(node);
    });
  const { name, description, shortDescription, illustration, heroImage, accessType } =
    data.circleSeriesBySlug;

  const title = `${name} | ${$t(messages.header.title)}`;
  const descriptionFallback = $t(messages.header.descriptionFallback);
  const formattedAccessType = $t(formatSeriesAccessType(accessType, name));
  return (
    <Wrapper>
      <Head>
        <title>{title}</title>
        <meta property="og:title" content={title} />
        <meta property="og:description" content={shortDescription ?? descriptionFallback} />
        {illustration && <meta property="og:image" content={illustration} />}
      </Head>

      <HeroWrapper>
        <HeaderTextWrapper>
          <BackButton />
          <HeadingText50 marginBottom={12}>{name}</HeadingText50>
          {sessionsCount > 0 ? (
            <ComponentTextMedium fontColor="gray65" css={{ marginBottom: 16 }}>
              <FormattedMessage
                defaultMessage="{sessionsCount} sessions"
                values={{ sessionsCount }}
              />
              {formattedAccessType ? `  ·  ${formattedAccessType}` : ''}
            </ComponentTextMedium>
          ) : (
            <ComponentTextMedium fontColor="gray65" css={{ marginBottom: 16 }}>
              {formattedAccessType}
            </ComponentTextMedium>
          )}
          <BodyTextMedium css={{ lineHeight: '1.75rem' }}>{description}</BodyTextMedium>
        </HeaderTextWrapper>
        <HeaderImageWrapper>
          <HeaderImage src={heroImage || ''} />
        </HeaderImageWrapper>
      </HeroWrapper>
      <HeadingText40 marginTop={40} marginBottom={56}>
        {sessionsCount > 0 ? (
          <FormattedMessage defaultMessage="Upcoming Circles" />
        ) : (
          <FormattedMessage defaultMessage="Registration coming soon" />
        )}
      </HeadingText40>
      <Grid lgMode="3columns">
        {topics.map((topic: CircleTopicFragment) =>
          topic?.sessions?.totalCount > 0 ? (
            <GridItem lg={1} md={2} column key={`session-card-${topic.id}`}>
              <TopicLinkCard key={topic.id} topic={topic} />
            </GridItem>
          ) : null,
        )}
      </Grid>
    </Wrapper>
  );
};

const SeriesRoute: React.FC<unknown> = () => {
  const { seriesSlug } = useParams<SeriesProps>();

  if (!seriesSlug) {
    return <Redirect to="/" />;
  }

  return <SeriesPage seriesSlug={seriesSlug} />;
};

export default SeriesRoute;
